/**
 * Sauvegarde de la reponse d'un eleve.
 *
 * Implementation unique, partagee par le bouton "Executer" et le bouton
 * "Enregistrer". Elle remplace deux cascades de repli identiques qui, apres
 * plusieurs echecs, reecrivaient la reponse de l'eleve avec la chaine "test" et
 * annoncaient malgre tout une sauvegarde reussie.
 *
 * Regle retenue : une tentative, puis un repli local honnete. On ne remplace
 * jamais le travail de l'eleve par un contenu de substitution.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserApi } from '../../../api/user/userApp';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';
import { TUserAnswer } from '../../../models/user/UserProgression';
import { HtmlCssWorkspace } from '../core/types';
import { serializeWorkspace } from '../core/workspace';

/** Issue d'une tentative de sauvegarde. */
export type SaveOutcome =
  /** Enregistre sur le serveur. */
  | { status: 'saved' }
  /** Serveur injoignable : conserve dans le navigateur, a resynchroniser. */
  | { status: 'local'; reason: string }
  /** Ni serveur ni stockage local disponibles. */
  | { status: 'failed'; reason: string };

const storageKey = (lessonId: number) => `htmlcss_lesson_${lessonId}`;

/** Instantane conserve dans le navigateur quand le serveur est injoignable. */
interface LocalSnapshot {
  html: string;
  css: string;
  savedAt: string;
  lessonId: number;
}

/** Relit l'instantane local d'une lecon, s'il existe. */
export const readLocalSnapshot = (lessonId: number): LocalSnapshot | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(lessonId));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<LocalSnapshot>;
    if (typeof parsed.html !== 'string' || typeof parsed.css !== 'string') return null;

    return {
      html: parsed.html,
      css: parsed.css,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
      lessonId,
    };
  } catch {
    // Stockage indisponible (navigation privee, quota) : l'absence
    // d'instantane n'est pas une erreur bloquante.
    return null;
  }
};

/** Efface l'instantane local une fois la reponse acceptee par le serveur. */
const clearLocalSnapshot = (lessonId: number): void => {
  try {
    window.localStorage.removeItem(storageKey(lessonId));
  } catch {
    // Sans consequence : l'instantane sera ecrase a la prochaine sauvegarde.
  }
};

/** Conserve la reponse dans le navigateur en dernier recours. */
const writeLocalSnapshot = (lessonId: number, workspace: HtmlCssWorkspace): boolean => {
  try {
    const snapshot: LocalSnapshot = {
      html: workspace.html,
      css: workspace.css,
      savedAt: new Date().toISOString(),
      lessonId,
    };
    window.localStorage.setItem(storageKey(lessonId), JSON.stringify(snapshot));
    return true;
  } catch {
    return false;
  }
};

const describeError = (error: any): string =>
  error?.response?.data ?? error?.message ?? 'Erreur inconnue';

interface UseHtmlCssPersistenceOptions {
  lessonId: number;
  /** Desactive toute sauvegarde (mode enseignant ou apercu d'administration). */
  disabled?: boolean;
}

export interface HtmlCssPersistence {
  save: (workspace: HtmlCssWorkspace) => Promise<SaveOutcome>;
  saving: boolean;
  /**
   * Declare l'identifiant de la reponse existante, requis pour que la
   * sauvegarde mette a jour au lieu de creer un doublon.
   *
   * L'identifiant n'est volontairement pas relu : il vit dans une ref, et une
   * ref lue pendant le rendu renverrait toujours sa valeur initiale.
   */
  setAnswerId: (id: number) => void;
}

export const useHtmlCssPersistence = ({
  lessonId,
  disabled = false,
}: UseHtmlCssPersistenceOptions): HtmlCssPersistence => {
  const { saveUserAnswerById } = useUserApi();
  const { getUserAppId } = useTilmiAuth();

  // Ces fonctions ne sont pas memoisees par leurs hooks : les lire via une ref
  // garde `save` stable, sinon chaque rendu en changerait l'identite et
  // relancerait les effets qui en dependent.
  const apiRef = useRef({ saveUserAnswerById, getUserAppId });
  useEffect(() => {
    apiRef.current = { saveUserAnswerById, getUserAppId };
  });

  const [saving, setSaving] = useState(false);

  // L'identifiant vit dans une ref : il est relu a l'interieur de `save` sans
  // en faire varier l'identite, ce qui evite de recreer les rappels qui en
  // dependent a chaque sauvegarde.
  const answerIdRef = useRef(0);

  const setAnswerId = useCallback((id: number) => {
    answerIdRef.current = id;
  }, []);

  const save = useCallback(
    async (workspace: HtmlCssWorkspace): Promise<SaveOutcome> => {
      if (disabled) return { status: 'saved' };

      const userAppId = apiRef.current.getUserAppId();
      if (!userAppId || !lessonId) {
        return { status: 'failed', reason: 'Session utilisateur indisponible' };
      }

      const payload: Partial<TUserAnswer> = {
        id: answerIdRef.current || 0,
        userapp_id: userAppId,
        lesson_id: lessonId,
        workspace: serializeWorkspace(workspace),
      };

      setSaving(true);
      try {
        const response = await apiRef.current.saveUserAnswerById(lessonId, payload);
        if (response?.data?.id) {
          answerIdRef.current = response.data.id;
        }
        clearLocalSnapshot(lessonId);
        return { status: 'saved' };
      } catch (error: any) {
        const reason = describeError(error);
        // Le serveur a refuse : on preserve le travail localement plutot que de
        // le perdre, et on le dit sans pretendre qu'il est enregistre.
        return writeLocalSnapshot(lessonId, workspace)
          ? { status: 'local', reason }
          : { status: 'failed', reason };
      } finally {
        setSaving(false);
      }
    },
    [disabled, lessonId]
  );

  return { save, saving, setAnswerId };
};
